/* eslint-disable jsx-a11y/no-autofocus -- intentional UX: dialogs/inputs need immediate focus */
/**
 * CompanyInfoStep Section Sub-components
 *
 * Extracted from CompanyInfoStep to keep each function under 50 lines.
 */

import { type ChangeEvent } from 'react'

import { useCurrencies } from '../../hooks/use-currencies'
import { Input, CountrySelect, StateSelect, PhoneInput, DatePicker, Select, QuickPasteAddress } from '../ui'
import { formatDateToString } from './CompanyInfoStepHelpers'

import type { Timezone, State } from '../../services/geolocation-service'
import type { CompanySetupData } from '../../types/company-setup'
import type { PhoneValue } from '../ui'

type CompanyInfo = CompanySetupData['companyInfo']

// Country-specific label overrides
const COUNTRY_LABELS: Record<string, string> = {
  'GB': 'County',
  'IN': 'State',
  'US': 'State',
  'CA': 'Province',
  'AU': 'State/Territory',
  'NZ': 'Region',
  'IE': 'County',
}

export { COUNTRY_LABELS }

// =============================================================================
// Basic Information Section
// =============================================================================

interface BasicInfoSectionProps {
  localInfo: CompanyInfo
  errors: Record<string, string>
  timezones: Timezone[]
  isLoadingTimezones: boolean
  onChange: (field: keyof CompanyInfo, value: string) => void
}

export function BasicInfoSection({
  localInfo,
  errors,
  timezones,
  isLoadingTimezones,
  onChange,
}: BasicInfoSectionProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>

      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="name">
          Company Name <span className="text-red-500">*</span>
        </label>
        <Input
          autoFocus
          autoCapitalize="words"
          autoComplete="organization"
          error={errors.name}
          id="name"
          inputMode="text"
          name="name"
          placeholder="e.g., Acme Corporation Pvt Ltd"
          type="text"
          value={localInfo.name}
          onChange={(e): void => onChange('name', e.target.value)}
        />
      </div>

      <FiscalYearFields errors={errors} localInfo={localInfo} onChange={onChange} />
      <CountryAndCurrencyFields localInfo={localInfo} onChange={onChange} />
      <TimezoneField isLoadingTimezones={isLoadingTimezones} localInfo={localInfo} timezones={timezones} onChange={onChange} />
    </div>
  )
}

interface FiscalYearFieldsProps {
  localInfo: CompanyInfo
  errors: Record<string, string>
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function FiscalYearFields({ localInfo, errors, onChange }: FiscalYearFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="fiscalYearStart">
          Fiscal Year Start <span className="text-red-500">*</span>
        </label>
        <DatePicker
          dateFormat="dd/MM/yyyy"
          error={errors.fiscalYearStart}
          id="fiscalYearStart"
          placeholder="Select start date"
          value={localInfo.fiscalYearStart ? new Date(localInfo.fiscalYearStart) : null}
          onChange={(date): void => onChange('fiscalYearStart', date ? formatDateToString(date) : '')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="fiscalYearEnd">
          Fiscal Year End <span className="text-red-500">*</span>
        </label>
        <DatePicker
          dateFormat="dd/MM/yyyy"
          error={errors.fiscalYearEnd}
          id="fiscalYearEnd"
          placeholder="Select end date"
          value={localInfo.fiscalYearEnd ? new Date(localInfo.fiscalYearEnd) : null}
          onChange={(date): void => onChange('fiscalYearEnd', date ? formatDateToString(date) : '')}
        />
      </div>
    </div>
  )
}

interface CountryAndCurrencyFieldsProps {
  localInfo: CompanyInfo
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function CountryAndCurrencyFields({ localInfo, onChange }: CountryAndCurrencyFieldsProps): JSX.Element {
  const { currencies: currencyOptions } = useCurrencies()
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="company-country">
          Country
        </label>
        <CountrySelect
          id="company-country"
          placeholder="Select country"
          value={localInfo.country ?? 'IN'}
          onChange={(code): void => { if (code) { onChange('country', code) } }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="base-currency">
          Base Currency <span className="text-red-500">*</span>
        </label>
        <Select
          helperText="Auto-selected based on country — change it if this isn't right"
          id="base-currency"
          options={currencyOptions}
          value={localInfo.currency}
          onChange={(value): void => onChange('currency', value)}
        />
      </div>
    </div>
  )
}

interface TimezoneFieldProps {
  localInfo: CompanyInfo
  timezones: Timezone[]
  isLoadingTimezones: boolean
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function TimezoneField({ localInfo, timezones, isLoadingTimezones, onChange }: TimezoneFieldProps): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="timezone">
        Timezone <span className="text-red-500">*</span>
      </label>
      <Select
        hideMessage
        disabled={isLoadingTimezones}
        id="timezone"
        options={
          isLoadingTimezones
            ? [{ value: '', label: 'Loading timezones...' }]
            : timezones.map((tz) => ({ value: tz.identifier, label: `${tz.description} (${tz.utcOffset})` }))
        }
        value={localInfo.timezone}
        onChange={(value): void => onChange('timezone', value)}
      />
    </div>
  )
}

// =============================================================================
// Address Section
// =============================================================================

interface AddressSectionProps {
  localInfo: CompanyInfo
  states: State[]
  stateFieldLabel: string
  postalCodeLabel: string
  postalCodeExample: string
  postalCodeInputMode: 'numeric' | 'text'
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function applyParsedAddress(
  parsed: { line1: string; line2: string; city: string; state: string; stateCode: string; postalCode: string },
  onChange: (field: keyof CompanyInfo, value: string) => void,
): void {
  if (parsed.line1) { onChange('building', parsed.line1) }
  if (parsed.line2) { onChange('address', parsed.line2) }
  if (parsed.city) { onChange('city', parsed.city) }
  if (parsed.stateCode) { onChange('state', parsed.stateCode) }
  if (parsed.postalCode) { onChange('postalCode', parsed.postalCode) }
}

function AddressLineFields({ localInfo, onChange }: { localInfo: CompanyInfo; onChange: (field: keyof CompanyInfo, value: string) => void }): JSX.Element {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="building">
          Building / Floor
        </label>
        <Input
          autoCapitalize="words"
          autoComplete="address-line1"
          id="building"
          inputMode="text"
          name="building"
          placeholder="e.g., 5th Floor, Tower A"
          type="text"
          value={localInfo.building ?? ''}
          onChange={(e): void => onChange('building', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="address">
          Street Address
        </label>
        <Input
          autoCapitalize="words"
          autoComplete="address-line2"
          id="address"
          inputMode="text"
          name="address"
          placeholder="e.g., 123 MG Road"
          type="text"
          value={localInfo.address ?? ''}
          onChange={(e): void => onChange('address', e.target.value)}
        />
      </div>
    </>
  )
}

function StateField({ localInfo, stateFieldLabel, onChange }: { localInfo: CompanyInfo; stateFieldLabel: string; onChange: (field: keyof CompanyInfo, value: string) => void }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="state">
        {stateFieldLabel}
      </label>
      <StateSelect
        countryCode={localInfo.country ?? ''}
        id="state"
        placeholder={`Select ${stateFieldLabel}`}
        value={localInfo.state ?? ''}
        onChange={(code, _name, _fullId): void => onChange('state', code ?? '')}
      />
    </div>
  )
}

export function AddressSection({
  localInfo, states, stateFieldLabel, postalCodeLabel, postalCodeExample, postalCodeInputMode, onChange,
}: AddressSectionProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <h3 className="text-lg font-semibold text-foreground">Address</h3>
      <QuickPasteAddress
        id="company-address-paste"
        includeGSTIN={localInfo.country === 'IN'}
        onParse={(parsed) => applyParsedAddress(parsed, onChange)}
      />
      <AddressLineFields localInfo={localInfo} onChange={onChange} />
      {states.length > 0 && <StateField localInfo={localInfo} stateFieldLabel={stateFieldLabel} onChange={onChange} />}
      <CityAndPostalFields
        localInfo={localInfo}
        postalCodeExample={postalCodeExample}
        postalCodeInputMode={postalCodeInputMode}
        postalCodeLabel={postalCodeLabel}
        onChange={onChange}
      />
    </div>
  )
}

interface CityAndPostalFieldsProps {
  localInfo: CompanyInfo
  postalCodeLabel: string
  postalCodeExample: string
  postalCodeInputMode: 'numeric' | 'text'
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function CityAndPostalFields({
  localInfo,
  postalCodeLabel,
  postalCodeExample,
  postalCodeInputMode,
  onChange,
}: CityAndPostalFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="city">
          City
        </label>
        <Input
          autoCapitalize="words"
          autoComplete="address-level2"
          id="city"
          inputMode="text"
          name="city"
          placeholder="City"
          type="text"
          value={localInfo.city ?? ''}
          onChange={(e): void => onChange('city', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="postalCode">
          {postalCodeLabel}
        </label>
        <Input
          autoComplete="postal-code"
          id="postalCode"
          inputMode={postalCodeInputMode}
          name="postalCode"
          placeholder={postalCodeExample}
          type="text"
          value={localInfo.postalCode ?? ''}
          onChange={(e): void => onChange('postalCode', e.target.value)}
        />
      </div>
    </div>
  )
}

// =============================================================================
// Contact Information Section
// =============================================================================

interface ContactInfoSectionProps {
  localInfo: CompanyInfo
  errors: Record<string, string>
  onPhoneChange: (value: PhoneValue | null) => void
  onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void
  onEmailPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
  onChange: (field: keyof CompanyInfo, value: string) => void
}

function PhoneEmailRow({ localInfo, errors, onPhoneChange, onEmailChange, onEmailPaste }: Omit<ContactInfoSectionProps, 'onChange'>): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="phone">Phone Number</label>
        <PhoneInput
          autoComplete="tel"
          id="phone"
          name="phone"
          placeholder="Enter phone number"
          value={localInfo.phone ?? null}
          onChange={onPhoneChange}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="email">Email Address</label>
        <Input
          autoCapitalize="none"
          autoComplete="email"
          error={errors.email}
          id="email"
          inputMode="email"
          name="email"
          placeholder="contact@company.com"
          type="email"
          value={localInfo.email ?? ''}
          onChange={onEmailChange}
          onPaste={onEmailPaste}
        />
      </div>
    </div>
  )
}

function WebsiteField({ localInfo, errors, onChange }: { localInfo: CompanyInfo; errors: Record<string, string>; onChange: (field: keyof CompanyInfo, value: string) => void }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="website">Website</label>
      <Input
        autoCapitalize="none"
        autoComplete="url"
        error={errors.website}
        id="website"
        inputMode="url"
        name="website"
        placeholder="https://www.company.com"
        type="url"
        value={localInfo.website ?? ''}
        onChange={(e): void => onChange('website', e.target.value)}
      />
    </div>
  )
}

export function ContactInfoSection(props: ContactInfoSectionProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
      <PhoneEmailRow
        errors={props.errors}
        localInfo={props.localInfo}
        onEmailChange={props.onEmailChange}
        onEmailPaste={props.onEmailPaste}
        onPhoneChange={props.onPhoneChange}
      />
      <WebsiteField errors={props.errors} localInfo={props.localInfo} onChange={props.onChange} />
    </div>
  )
}

// =============================================================================
// Tax Information Section
// =============================================================================

interface TaxInfoSectionProps {
  localInfo: CompanyInfo
  errors: Record<string, string>
  onGSTINChange: (e: ChangeEvent<HTMLInputElement>) => void
  onPANChange: (e: ChangeEvent<HTMLInputElement>) => void
  onGSTINPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
  onPANPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void
}

function GSTINField({ localInfo, errors, onGSTINChange, onGSTINPaste }: Pick<TaxInfoSectionProps, 'localInfo' | 'errors' | 'onGSTINChange' | 'onGSTINPaste'>): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="gstin">
        GSTIN (GST Identification Number)
      </label>
      <Input
        autoCapitalize="characters"
        autoComplete="off"
        error={errors.gstin}
        helperText="Format: NNAAAAANNNNANAX (15 chars) • Auto-fills PAN when entered • Optional"
        id="gstin"
        inputMode="text"
        maxLength={15}
        name="gstin"
        placeholder="22AAAAA0000A1Z5"
        type="text"
        value={localInfo.gstin ?? ''}
        onChange={onGSTINChange}
        onPaste={onGSTINPaste}
      />
    </div>
  )
}

function PANField({ localInfo, errors, onPANChange, onPANPaste }: Pick<TaxInfoSectionProps, 'localInfo' | 'errors' | 'onPANChange' | 'onPANPaste'>): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-secondary mb-2" htmlFor="pan">
        PAN (Permanent Account Number)
      </label>
      <Input
        autoCapitalize="characters"
        autoComplete="off"
        error={errors.pan}
        helperText="Format: AAAAA9999A (5 letters, 4 digits, 1 letter) • Auto-filled from GSTIN"
        id="pan"
        inputMode="text"
        maxLength={10}
        name="pan"
        placeholder="ABCDE1234F"
        type="text"
        value={localInfo.pan ?? ''}
        onChange={onPANChange}
        onPaste={onPANPaste}
      />
    </div>
  )
}

export function TaxInfoSection(props: TaxInfoSectionProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <h3 className="text-lg font-semibold text-foreground">Tax & Business Identifiers</h3>
      <GSTINField errors={props.errors} localInfo={props.localInfo} onGSTINChange={props.onGSTINChange} onGSTINPaste={props.onGSTINPaste} />
      <PANField errors={props.errors} localInfo={props.localInfo} onPANChange={props.onPANChange} onPANPaste={props.onPANPaste} />
    </div>
  )
}

// =============================================================================
// Actions
// =============================================================================

interface CompanyInfoActionsProps {
  onBack: () => void
  onNext: () => void
}

export function CompanyInfoActions({ onBack, onNext }: CompanyInfoActionsProps): JSX.Element {
  return (
    <div className="flex justify-between pt-6 border-t border-border">
      <button
        className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:bg-surface-secondary transition-colors font-medium"
        type="button"
        onClick={onBack}
      >
        Back
      </button>
      <button
        className="px-3 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        type="button"
        onClick={onNext}
      >
        Next: Fiscal Periods
      </button>
    </div>
  )
}
